/**
 * One customer: their people, their fleet, their tickets, their invoices, and the two plans that
 * raise some of those by themselves.
 *
 * The port of `Views/CustomerDetailPage.xaml` (798 lines) + `CustomerDetailViewModel` (880). The
 * section order is the XAML's, and three of its rules are the reason the page is shaped this way:
 *
 *   * ARCHIVING AND PERMANENT DELETION BOTH LIVE HERE, and nowhere else. A list row shows a name and
 *     a device count, which is not enough to decide on; this page shows the devices, people, open
 *     tickets and invoices that are about to be stood down.
 *   * THE DIAGNOSTICS CARD SITS IMMEDIATELY AFTER THE APPROVAL QUEUE. A machine that failed to
 *     enroll never reaches Pending, so those two cards together answer the whole of "this PC should
 *     be here and is not" - one lists the ones waiting for you, the other the ones that never
 *     arrived.
 *   * A RECURRING SECTION'S CHANGE REFRESHES ONLY THE TICKETS AND INVOICES. Saving a template raises
 *     what it owes, so those two lists go stale - but a full page refresh would discard whatever
 *     editor is open elsewhere on the page, which is the mistake `MonitorChanged` made on the
 *     Network page.
 */
import { insets, type Container, type StackPanel } from 'webwin';
import * as auth from '../api/auth.js';
import * as crm from '../api/crm.js';
import * as hub from '../api/hub.js';
import { describeError } from '../api/http.js';
import {
  ApprovalStatus,
  DiagnosticStatus,
  InvoiceStatus,
  ManagementChannel,
  TicketPriority,
  type AgentDiagnosticDto,
  type CustomerDto,
  type DeviceDto,
  type DiagnosticCheck,
  type EmployeeDto,
  type InvoiceDto,
  type SnmpMonitorDto,
  type TicketDto
} from '../contracts.gen.js';
import { download, safeFileName } from '../download.js';
import { join, localDate, localDateTime, money, plainDate, plural, trim } from '../format.js';
import { isFinished } from '../rules.js';
import { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip, type ChipTone } from '../ui/Chip.js';
import { confirm, tell } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import { button, buttonRow, caption, card, checkBox, comboBox, field, fieldRow, fillWith, FONT, GAP, h1, h2, hstack, inlineRow, muted, relayout, scrollPage, splitRow, stackOf, text, textBox, value, vstack } from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { recurringInvoicesSection } from './sections/recurring-invoices.js';
import { recurringTicketsSection } from './sections/recurring-tickets.js';
import { androidPhonesSection } from './sections/android-phones.js';
import { windowsPoliciesSection } from './sections/windows-policies.js';
import { ticketCard } from './sections/ticket-card.js';
import { invoiceCard } from './sections/invoice-card.js';
import { uninvoicedSection } from './sections/uninvoiced.js';
import { attachmentsSection } from './sections/attachments.js';

const PRIORITY_NAMES = ['Low', 'Normal', 'High', 'Urgent'] as const;
const PRIORITIES = [
  TicketPriority.Low,
  TicketPriority.Normal,
  TicketPriority.High,
  TicketPriority.Urgent
] as const;

export function customerDetailPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      const customerId = Number(ctx.params['id'] ?? 0);
      if (!Number.isFinite(customerId) || customerId <= 0) {
        ctx.go('/customers', { replace: true });
        return;
      }

      let customer: CustomerDto | null = null;
      let employees: EmployeeDto[] = [];
      let allTickets: TicketDto[] = [];
      let showClosedTickets = false;
      let editingEmployeeId = 0;
      let diagnosticsExpanded = false;
      const expandedReports = new Set<number>();

      const status = new StatusLine();
      const { view, content } = scrollPage();
      const page = vstack({ spacing: GAP.lg });
      content.controls.add(page);
      host.controls.add(view);

      // =================================================================
      //  Header, banners and lifecycle
      // =================================================================

      const backLink = link('← Customers', () => ctx.go('/customers'));
      const nameLabel = h1('Customer');
      const addressLabel = muted('');

      const editButton = button('Edit customer', () => beginEditCustomer());
      const archiveButton = button('Archive', () => void archive());
      const purgeButton = button('Delete permanently', () => beginPurge());

      // Actions on a wrapping row under the name, not beside it: three buttons beside a long name left
      // the name a third of a phone's width and broke it mid-word.
      const headerActions = buttonRow();
      headerActions.controls.add(editButton);
      headerActions.controls.add(archiveButton);
      headerActions.controls.add(purgeButton);

      const headerRow = stackOf(vstack({ spacing: GAP.sm }), nameLabel, addressLabel, headerActions);

      // The archived banner. The record is complete and readable; what changed is that its agents
      // have stood down and its portal logins are locked out - neither of which is visible from the
      // page itself, which is why the banner says both.
      const archivedBanner = card({ tint: 'warn', bordered: true, visible: false });
      {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          text('This customer is archived', { font: FONT.bold, role: 'warn' }),
          muted(
            "Everything is retained. Its devices' agents have stood down and its portal logins are locked out until it is restored."
          )
        );
        const row = splitRow({ align: 'center' });
        fillWith(row, left);
        row.controls.add(button('Restore', () => void restore()));
        archivedBanner.controls.add(row);
      }

      // The typed-name confirmation. This is the one action on the page that cannot be undone, and a
      // dialog that needs only a click gets clicked.
      const purgePrompt = muted('');
      const purgeLabel = caption('');
      const purgeNameBox = textBox({ width: 360, hAlign: 'start' });
      const purgeError = muted('', 'danger');
      const purgeConfirm = button('Delete permanently', () => void purge());
      const purgePanel = card({ visible: false, tint: 'card' });
      purgePanel.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          text('Delete permanently', { font: FONT.h2, role: 'danger' }),
          purgePrompt,
          purgeLabel,
          purgeNameBox,
          purgeError,
          stackOf(
            buttonRow(),
            purgeConfirm,
            button('Cancel', () => cancelPurge())
          )
        )
      );
      purgeNameBox.on('textchanged', () => syncPurgeButton());

      // =================================================================
      //  Contact, and the customer editor
      // =================================================================

      const contactName = text('');
      const contactEmail = muted('');
      const contactPhone = muted('');
      const termsLabel = value('');
      const contactCard = card();
      contactCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.xs }),
          h2('Contact'),
          contactName,
          contactEmail,
          contactPhone,
          // Wraps: an hstack reports its content's width, so on a phone the second link was simply
          // off the edge of the card.
          stackOf(
            buttonRow({ spacing: GAP.md, margin: insets(0, GAP.sm, 0, 0) }),
            termsLabel,
            button('Billing →', () => ctx.go(`/billing/${customerId}`)),
            button('Network →', () => ctx.go(`/network/${customerId}`))
          )
        )
      );

      const custName = textBox();
      const custContact = textBox();
      const custEmail = textBox();
      const custPhone = textBox();
      const custAddress = textBox();
      const custTerms = textBox();
      const custRate = moneyBox();
      const customerEditor = card({ visible: false });
      customerEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Edit customer'),
          fieldRow(field('Name', custName), field('Contact name', custContact)),
          fieldRow(field('Email', custEmail), field('Phone', custPhone)),
          field('Address', custAddress),
          fieldRow(field('Billing terms', custTerms), field('Default rate per hour', custRate.row)),
          stackOf(
            buttonRow(),
            button('Save', () => void saveCustomer(), true),
            button('Cancel', () => closeCustomerEditor())
          )
        )
      );

      // =================================================================
      //  Enroll a device
      // =================================================================

      const synologyUrl = textBox({ readOnly: true });
      const synologyToken = textBox({ readOnly: true });
      const synologyPanel = card({ tint: 'alt', visible: false });
      synologyPanel.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          muted(
            'Install the Jetsonsoft CRM Agent package (.spk) on the Synology NAS or router via Package Center → Manual Install, and paste these into its install wizard. The device then appears below for approval, like any other enrollment.'
          ),
          field('Server URL', synologyUrl),
          field('Enrollment token', synologyToken)
        )
      );

      const enrollCard = card();
      enrollCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          h2('Enroll a device'),
          muted(
            "Download a ready-to-run installer for this customer. Save it to a USB drive and run it (double-click, allow the admin prompt) on any of this customer's machines, now or later. It installs the agent and enrolls the device under this customer only - each appears below for approval before it's managed."
          ),
          stackOf(
            buttonRow(),
            button('Download installer (.cmd)', () => void downloadInstaller(), true),
            button('Download offline bundle (.zip)', () => void downloadBundle())
          ),
          muted(
            'The .cmd is tiny and downloads the agent when run (needs internet). The offline bundle (~55 MB) includes the agent, so it installs with no download - the PC still needs to reach the server to enroll.'
          ),
          button('Synology package setup…', () => void toggleSynology()),
          synologyPanel
        )
      );

      // =================================================================
      //  Devices awaiting approval
      // =================================================================

      const pendingHost = vstack({ spacing: GAP.md });
      const pendingCard = card({ tint: 'warn', visible: false });
      pendingCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          h2('Devices awaiting approval'),
          muted(
            'Newly-enrolled devices are not managed until you approve them. Assign employees during approval.'
          ),
          pendingHost
        )
      );

      // =================================================================
      //  Agents that could not enroll
      // =================================================================

      const diagnosticsSummary = h2('Agents that could not enroll');
      const diagnosticsToggle = button('Show', () => toggleDiagnostics());
      const diagnosticsHost = vstack({ spacing: GAP.md, visible: false });
      const diagnosticsCard = card({ visible: false });
      {
        const head = splitRow({ align: 'center' });
        fillWith(head, diagnosticsSummary);
        head.controls.add(diagnosticsToggle);
        diagnosticsCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), head, diagnosticsHost));
      }

      // =================================================================
      //  Employees
      // =================================================================

      const employeeEditorTitle = h2('New employee');
      const empName = textBox();
      const empEmail = textBox();
      const empPhone = textBox();
      const empTitle = textBox();
      const employeeEditor = card({ visible: false });
      employeeEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          employeeEditorTitle,
          fieldRow(field('Name', empName), field('Job title', empTitle)),
          fieldRow(field('Email', empEmail), field('Phone', empPhone)),
          stackOf(
            buttonRow(),
            button('Save', () => void saveEmployee(), true),
            button('Cancel', () => closeEmployeeEditor())
          )
        )
      );

      const employeeList = new CardList<EmployeeDto>({
        key: e => e.id,
        build: e => buildEmployeeCard(e)
      });
      const employeesEmpty = new EmptyState('Nobody is listed for this customer yet.');
      const employeesHeader = sectionHeader('Employees', button('Add employee', () => beginNewEmployee()));

      // =================================================================
      //  Devices and monitors
      // =================================================================

      const deviceList = new CardList<DeviceDto>({
        key: d => d.id,
        build: d => buildDeviceCard(d)
      });
      const devicesEmpty = new EmptyState('No managed devices yet.');
      const devicesHeader = sectionHeader('Devices');

      const monitorList = new CardList<SnmpMonitorDto>({
        key: m => m.id,
        build: m => buildMonitorCard(m)
      });
      const monitorSection = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        sectionHeader('Monitored devices'),
        monitorList.panel
      );

      // =================================================================
      //  Tickets
      // =================================================================

      const ticketSummary = value('', { font: FONT.small, role: 'muted' });
      const closedToggle = button('Show closed', () => toggleClosedTickets());
      const ticketsHeader = (() => {
        const row = splitRow({ align: 'center' });
        const left = inlineRow({ spacing: GAP.md });
        left.controls.add(value('Tickets', { font: FONT.h2 }));
        left.controls.add(ticketSummary);
        fillWith(row, left);
        row.controls.add(closedToggle);
        row.controls.add(button('New ticket', () => beginNewTicket(), true));
        return row;
      })();

      const ticketTitle = textBox();
      const ticketDescription = textBox();
      const ticketPriority = comboBox<string>([...PRIORITY_NAMES], { selectedIndex: 1 });
      const ticketReporter = comboBox<string>([]);
      const ticketEditor = card({ visible: false });
      ticketEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('New ticket'),
          field('Title', ticketTitle),
          field('Description', ticketDescription),
          fieldRow(field('Priority', ticketPriority), field('Reported by (optional)', ticketReporter)),
          stackOf(
            buttonRow(),
            button('Raise ticket', () => void saveTicket(), true),
            button('Cancel', () => closeTicketEditor())
          )
        )
      );

      const ticketList = new CardList<TicketDto>({
        key: t => t.id,
        build: t => buildTicketCard(t)
      });
      const ticketsEmpty = new EmptyState('No tickets yet.');

      // =================================================================
      //  Contract pricing
      // =================================================================

      const pricingItems = comboBox<string>([]);
      const pricingAmount = moneyBox({ width: 120, hAlign: 'start' });
      let catalogIds: number[] = [];
      const pricingList = new CardList<import('../contracts.gen.js').CustomerCatalogPriceDto>({
        key: p => p.catalogItemId,
        build: p => buildPriceCard(p)
      });
      const pricingEmpty = new EmptyState('This customer pays the standard price for everything.');
      const pricingEditor = card();
      pricingEditor.visible = false;
      pricingEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          muted(
            "A price set here beats the item's list price and the purchase markup, for this customer only."
          ),
          stackOf(
            hstack({ spacing: GAP.md, align: 'end' }),
            field('Item', pricingItems),
            field('Price', pricingAmount.row),
            button('Set price', () => void savePrice(), true)
          )
        )
      );
      const pricingBody = stackOf(
        vstack({ spacing: GAP.sm }),
        pricingEditor,
        pricingEmpty.panel,
        pricingList.panel
      );
      const pricingHeader = sectionHeader(
        'Contract pricing',
        button('Manage prices', () => void togglePricing())
      );

      // =================================================================
      //  Invoices
      // =================================================================

      const invoiceList = new CardList<InvoiceDto>({
        key: i => i.id,
        build: i => buildInvoiceCard(i)
      });
      const invoiceSection = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        sectionHeader('Invoices', button('Billing →', () => ctx.go(`/billing/${customerId}`))),
        invoiceList.panel
      );

      const uninvoiced = uninvoicedSection({
        ctx,
        customerId,
        onInvoiced: () => void refreshTicketsAndInvoices()
      });

      // =================================================================
      //  The two recurring sections
      // =================================================================

      const recurringTickets = recurringTicketsSection({
        ctx,
        customerFilterId: customerId,
        lockCustomer: true,
        onChanged: () => void refreshTicketsAndInvoices()
      });
      const recurringInvoices = recurringInvoicesSection({
        ctx,
        customerFilterId: customerId,
        lockCustomer: true,
        onChanged: () => void refreshTicketsAndInvoices()
      });

      // =================================================================
      //  Assembly, in the XAML's order
      // =================================================================

      page.controls.add(stackOf(vstack({ spacing: GAP.sm }), backLink, headerRow, status.label));
      page.controls.add(archivedBanner);
      page.controls.add(purgePanel);
      page.controls.add(contactCard);
      page.controls.add(customerEditor);
      const attachments = attachmentsSection({ ctx, owner: { customerId } });
      page.controls.add(attachments.panel);
      page.controls.add(enrollCard);
      const androidPhones = androidPhonesSection(ctx, customerId);
      page.controls.add(androidPhones.card);
      const windowsPolicies = windowsPoliciesSection(ctx, customerId, () => refresh());
      page.controls.add(windowsPolicies.card);
      page.controls.add(pendingCard);
      page.controls.add(diagnosticsCard);
      page.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          employeesHeader,
          employeeEditor,
          employeesEmpty.panel,
          employeeList.panel
        )
      );
      page.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), devicesHeader, devicesEmpty.panel, deviceList.panel)
      );
      page.controls.add(monitorSection);
      page.controls.add(
        stackOf(
          vstack({ spacing: GAP.sm }),
          ticketsHeader,
          ticketEditor,
          ticketsEmpty.panel,
          ticketList.panel
        )
      );
      page.controls.add(recurringTickets.panel);
      page.controls.add(stackOf(vstack({ spacing: GAP.sm }), pricingHeader, pricingBody));
      page.controls.add(uninvoiced.panel);
      page.controls.add(invoiceSection);
      page.controls.add(recurringInvoices.panel);

      // =================================================================
      //  Cards
      // =================================================================

      function sectionHeader(title: string, ...actions: import('webwin').Control[]): StackPanel {
        const row = splitRow({ align: 'center' });
        fillWith(row, h2(title));
        for (const action of actions) row.controls.add(action);
        return row;
      }

      function buildEmployeeCard(e: EmployeeDto): Card {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          value(e.fullName, { font: FONT.bold }),
          muted(join([e.title, e.email, e.phone]))
        );
        const actions = hstack({ spacing: GAP.sm });
        actions.controls.add(button('Edit', () => beginEditEmployee(e)));
        actions.controls.add(button('Remove', () => void removeEmployee(e)));

        const row = splitRow();
        fillWith(row, left);
        row.controls.add(actions);

        const shell = card({ padding: insets(12) });
        shell.controls.add(row);
        return shell;
      }

      function buildDeviceCard(d: DeviceDto): Card {
        const row = splitRow({ spacing: GAP.md });
        const phone = d.channel === ManagementChannel.Android;
        // A phone has no connection to be online or offline on; when it last reported is the fact.
        row.controls.add(phone ? chip('phone', 'info') : chip(d.presence?.isOnline ? 'online' : 'offline', d.presence?.isOnline ? 'ok' : 'neutral'));
        fillWith(
          row,
          stackOf(
            vstack({ spacing: GAP.xs }),
            value(d.name, { font: FONT.bold }),
            muted(
              phone
                ? join([d.primaryEmployeeName, d.os?.caption, d.mobile?.lastStatusReportUtc ? `checked in ${localDateTime(d.mobile.lastStatusReportUtc)}` : 'not checked in yet'])
                : join([d.presence?.loggedOnUser, d.os?.caption, d.agentVersion ? `agent ${d.agentVersion}` : null])
            )
          )
        );

        const shell = card({ padding: insets(12), cursor: 'pointer' });
        shell.controls.add(row);
        shell.onActivate(() => ctx.go(`/devices/${d.id}`));
        return shell;
      }

      function buildMonitorCard(m: SnmpMonitorDto): Card {
        const row = splitRow({ spacing: GAP.md });
        row.controls.add(chip(m.online ? 'online' : 'offline', m.online ? 'ok' : 'neutral'));
        fillWith(
          row,
          stackOf(
            vstack({ spacing: GAP.xs }),
            value(m.displayName, { font: FONT.bold }),
            muted(join([m.ipAddress, m.statusLabel]))
          )
        );

        const shell = card({ padding: insets(12), cursor: 'pointer' });
        shell.controls.add(row);
        shell.onActivate(() => ctx.go(`/monitors/${m.id}`));
        return shell;
      }

      function buildTicketCard(t: TicketDto): Card {
        return ticketCard(t, { onOpen: ticket => ctx.go(`/tickets/${ticket.id}`) });
      }

      function buildInvoiceCard(i: InvoiceDto): Card {
        return invoiceCard(i, { onOpen: invoice => ctx.go(`/invoices/${invoice.id}`) });
      }

      function buildPriceCard(p: import('../contracts.gen.js').CustomerCatalogPriceDto): Card {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          value(join([p.itemName, p.sku]), { font: FONT.bold }),
          muted(`standard ${money(p.standardUnitPrice)}`)
        );
        const actions = hstack({ spacing: GAP.sm });
        actions.controls.add(value(money(p.unitPrice), { font: FONT.bold }));
        actions.controls.add(
          button('Edit', async () => {
            if (!pricingEditor.visible) await togglePricing();
            const at = catalogIds.indexOf(p.catalogItemId);
            if (at >= 0) pricingItems.selectedIndex = at;
            pricingAmount.set(p.unitPrice);
          })
        );
        actions.controls.add(button('Remove', () => void clearPrice(p)));

        const row = splitRow({ align: 'center' });
        fillWith(row, left);
        row.controls.add(actions);

        const shell = card({ padding: insets(10) });
        shell.controls.add(row);
        return shell;
      }

      /**
       * One device awaiting approval, with the customer's employees to assign.
       *
       * Rebuilt from scratch on every refresh rather than diffed: the tick state is the technician's
       * working input, and it exists only until Approve is pressed - so there is nothing to preserve
       * across a reload, and a row that survived one would carry ticks made against a list of
       * employees that may have changed underneath it.
       */
      function buildPendingCard(d: DeviceDto): Card {
        const selected = new Set<number>();
        let primary: number | null = null;
        const primaryChecks = new Map<number, ReturnType<typeof checkBox>>();

        const rowStatus = new StatusLine();
        const shell = card({ padding: insets(12) });
        const body = vstack({ spacing: GAP.sm });

        body.controls.add(
          value(join([d.os?.machineName ?? d.name, d.os?.caption]), { font: FONT.bold })
        );
        body.controls.add(
          muted(join([d.presence?.loggedOnUser || '(no user)', `enrolled ${localDateTime(d.enrolledUtc)}`]))
        );

        if (employees.length > 0) {
          body.controls.add(caption('Assign employees (tick to assign, then mark one primary):'));
          for (const e of employees) {
            const assign = checkBox(join([e.fullName, e.title]) || e.fullName);
            const isPrimary = checkBox('primary');
            primaryChecks.set(e.id, isPrimary);

            assign.on('change', () => {
              if (assign.checked) selected.add(e.id);
              else {
                selected.delete(e.id);
                // An unassigned employee cannot be the primary contact for the machine.
                if (primary === e.id) {
                  primary = null;
                  isPrimary.checked = false;
                }
              }
            });

            isPrimary.on('change', () => {
              if (!isPrimary.checked) {
                if (primary === e.id) primary = null;
                return;
              }
              // Primary implies assigned, and only one at a time - the `OnPrimaryChosen` rule.
              assign.checked = true;
              selected.add(e.id);
              primary = e.id;
              for (const [id, box] of primaryChecks) if (id !== e.id) box.checked = false;
            });

            const line = inlineRow({ spacing: GAP.md });
            line.controls.add(assign);
            line.controls.add(isPrimary);
            body.controls.add(line);
          }
        }

        const actions = hstack({ spacing: GAP.sm });
        actions.controls.add(
          button(
            'Approve',
            () => void approve(d, [...selected], primary, rowStatus),
            true
          )
        );
        actions.controls.add(button('Reject', () => void reject(d, rowStatus)));
        body.controls.add(actions);
        body.controls.add(rowStatus.label);

        shell.controls.add(body);
        return shell;
      }

      /**
       * One uploaded `--diagnose` report.
       *
       * Collapsed by default and expanded per row: a report is a verdict plus a dozen checks plus up
       * to forty log lines, and 200 of them are kept per customer - rendering all of that for every
       * report would push the employees, devices and invoices off the page for any customer that has
       * ever had a failed install.
       */
      function buildDiagnosticCard(report: AgentDiagnosticDto): Card {
        const open = expandedReports.has(report.id);

        const heading = inlineRow();
        heading.controls.add(
          value(report.machineName || '(unnamed machine)', { font: FONT.bold })
        );
        // "Enrolled" means the machine had an identity when it reported, which is a different
        // question from whether anything is wrong - a report exists because somebody thought
        // something was. Its own chip, so the two are never conflated.
        heading.controls.add(
          report.enrolled ? chip('Enrolled', 'ok') : chip('Never enrolled', 'danger')
        );
        heading.controls.add(value(`collected ${localDateTime(report.collectedUtc)}`, {
          font: FONT.small,
          role: 'muted'
        }));

        const detail = join([
          report.agentVersion ? `agent ${report.agentVersion}` : null,
          report.osCaption,
          report.serverUrl ? `-> ${report.serverUrl}` : null,
          report.reportedFromIp ? `from ${report.reportedFromIp}` : null
        ]);

        const body = vstack({ spacing: GAP.sm, visible: open });
        for (const check of report.checks ?? []) body.controls.add(buildCheckLine(check));
        if ((report.logEntries ?? []).length > 0) {
          body.controls.add(caption('From the machine’s event log'));
          for (const entry of report.logEntries ?? []) {
            body.controls.add(
              muted(
                join([localDateTime(entry.whenUtc), entry.level, entry.message]),
                entry.level === 'Error' ? 'danger' : entry.level === 'Warning' ? 'warn' : 'muted'
              )
            );
          }
        }

        const toggle = button(open ? 'Hide detail' : 'Show detail', () => {
          const nowOpen = !body.visible;
          body.visible = nowOpen;
          toggle.text = nowOpen ? 'Hide detail' : 'Show detail';
          if (nowOpen) expandedReports.add(report.id);
          else expandedReports.delete(report.id);
          relayout(page);
        });

        const top = splitRow({ align: 'center' });
        fillWith(
          top,
          stackOf(
            vstack({ spacing: GAP.xs }),
            heading,
            // The agent composed the verdict; the CRM does not second-guess it, because the agent is
            // the only party that saw the machine.
            text(report.verdict || '', { font: FONT.body }),
            muted(detail)
          )
        );
        top.controls.add(toggle);

        const shell = card({ padding: insets(12) });
        shell.controls.add(stackOf(vstack({ spacing: GAP.sm }), top, body));
        return shell;
      }

      function buildCheckLine(check: DiagnosticCheck): StackPanel {
        // Unknown is painted apart from Ok, never with it: "the check could not run" and "the check
        // passed" are different facts, which is the whole reason the third state exists.
        const tone: ChipTone =
          check.status === DiagnosticStatus.Ok
            ? 'ok'
            : check.status === DiagnosticStatus.Warning
              ? 'warn'
              : check.status === DiagnosticStatus.Failed
                ? 'danger'
                : 'neutral';
        const marker =
          check.status === DiagnosticStatus.Ok
            ? 'OK'
            : check.status === DiagnosticStatus.Warning
              ? '!'
              : check.status === DiagnosticStatus.Failed
                ? 'FAIL'
                : '?';

        const row = splitRow({ spacing: GAP.sm, align: 'start' });
        row.controls.add(chip(marker, tone));
        fillWith(
          row,
          stackOf(
            vstack({ spacing: 0 }),
            value(check.name ?? '', { font: FONT.bold }),
            muted(check.detail ?? '')
          )
        );
        return row;
      }

      // =================================================================
      //  Lifecycle actions
      // =================================================================

      function isAdmin(): boolean {
        return (auth.currentUser()?.role ?? '').toLowerCase() === 'admin';
      }

      function applyCustomer(next: CustomerDto): void {
        customer = next;
        nameLabel.text = next.name;
        addressLabel.text = next.address || '';
        contactName.text = next.contactName || 'No contact named';
        contactEmail.text = next.contactEmail || '';
        contactPhone.text = next.contactPhone || '';
        termsLabel.text = `Terms: ${next.billingTerms || 'not set'} · ${money(next.defaultRate)}/hr`;

        archivedBanner.visible = next.archived;
        archiveButton.visible = !next.archived;
        // Only for an archived customer, and only for an admin - the same two conditions the purge
        // endpoint enforces, so the typed-name panel can never be opened for a call bound to 403.
        purgeButton.visible = next.archived && isAdmin();
        // Restoring while the panel is open would leave a confirmation up for an action the server
        // would now refuse.
        if (!next.archived) cancelPurge();

        purgePrompt.text =
          `This permanently deletes ${next.name} and everything under it - employees, devices and ` +
          'their history, tickets, logged time, charges, and any draft or voided invoices. Invoices ' +
          'that were issued or paid are kept: they record money billed, and you will find them under ' +
          '"Invoices from deleted customers" on the archived-customers list. This cannot be undone.';
        purgeLabel.text = `Type "${next.name}" to confirm`;
        syncPurgeButton();
        relayout(page);
      }

      async function archive(): Promise<void> {
        if (!customer) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Archive customer',
          message:
            `Archive ${customer.name}?\n\nIts people, devices, tickets, work and invoices are all kept. ` +
            "The devices' agents stand down and its portal logins stop working, both of which Restore " +
            'undoes.\n\nTo destroy the record for good, delete it permanently from this page once it is ' +
            'archived.'
        });
        if (!ok || ctx.cancelled()) return;

        status.busy('Archiving…');
        try {
          await crm.archiveCustomer(customerId);
          if (ctx.cancelled()) return;
          status.ok('Customer archived. Everything is retained - Restore undoes it.');
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function restore(): Promise<void> {
        status.busy('Restoring…');
        try {
          await crm.restoreCustomer(customerId);
          if (ctx.cancelled()) return;
          status.ok('Customer restored.');
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      function beginPurge(): void {
        purgeNameBox.text = '';
        purgeError.text = '';
        purgePanel.visible = true;
        syncPurgeButton();
        relayout(page);
        purgeNameBox.focus();
      }

      function cancelPurge(): void {
        purgeNameBox.text = '';
        purgeError.text = '';
        purgePanel.visible = false;
        relayout(page);
      }

      function syncPurgeButton(): void {
        purgeConfirm.enabled =
          customer !== null &&
          customer.archived &&
          purgeNameBox.text.trim() === (customer.name ?? '').trim();
      }

      async function purge(): Promise<void> {
        if (!customer || !customer.archived) return;
        purgeError.text = '';
        purgeConfirm.enabled = false;
        try {
          const result = await crm.purgeCustomer(customerId, purgeNameBox.text.trim());
          if (ctx.cancelled()) return;
          cancelPurge();
          // The record this page is about no longer exists, so there is nothing to stay for - but
          // the invoices that survived are worth a word on the way out, because nothing else in the
          // CRM would ever say they had been kept.
          if (result.retainedInvoiceCount > 0) {
            await tell(
              ctx.desktop,
              'Delete permanently',
              `Deleted ${result.customerName} permanently. ` +
                `${plural(result.retainedInvoiceCount, 'issued or paid invoice')} ${
                  result.retainedInvoiceCount === 1 ? 'is' : 'are'
                } kept under "Invoices from deleted customers" on the archived-customers list.`
            );
          }
          ctx.go('/customers', { replace: true });
        } catch (error) {
          if (ctx.cancelled()) return;
          purgeError.text = describeError(error);
          syncPurgeButton();
        }
      }

      // =================================================================
      //  Customer editor
      // =================================================================

      function beginEditCustomer(): void {
        if (!customer) return;
        custName.text = customer.name;
        custContact.text = customer.contactName;
        custEmail.text = customer.contactEmail;
        custPhone.text = customer.contactPhone;
        custAddress.text = customer.address;
        custTerms.text = customer.billingTerms;
        custRate.set(customer.defaultRate);
        customerEditor.visible = true;
        contactCard.visible = false;
        relayout(page);
        custName.focus();
      }

      function closeCustomerEditor(): void {
        customerEditor.visible = false;
        contactCard.visible = true;
        relayout(page);
      }

      async function saveCustomer(): Promise<void> {
        const name = custName.text.trim();
        if (!name) {
          status.error('Name is required.');
          return;
        }
        status.busy('Saving…');
        try {
          const updated = await crm.updateCustomer(customerId, {
            name,
            contactName: custContact.text.trim(),
            contactEmail: custEmail.text.trim(),
            contactPhone: custPhone.text.trim(),
            address: custAddress.text.trim(),
            billingTerms: custTerms.text.trim(),
            defaultRate: custRate.value() ?? 0
          });
          if (ctx.cancelled()) return;
          applyCustomer(updated);
          closeCustomerEditor();
          status.ok('Customer updated.');
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      // =================================================================
      //  Enrollment
      // =================================================================

      async function downloadInstaller(): Promise<void> {
        status.busy('Preparing the installer…');
        try {
          await download(
            `/api/customers/${customerId}/installer`,
            `Install-JetsonCRM-${safeFileName(customer?.name)}.cmd`
          );
          if (ctx.cancelled()) return;
          status.ok('Installer downloaded. Save it to USB, then run it (as Administrator) on the device.');
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function downloadBundle(): Promise<void> {
        status.busy("Building the offline bundle… it's large, so this takes a moment.");
        try {
          await download(
            `/api/customers/${customerId}/installer-bundle`,
            `JetsonCRM-Offline-${safeFileName(customer?.name)}.zip`
          );
          if (ctx.cancelled()) return;
          status.ok('Offline bundle downloaded.');
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function toggleSynology(): Promise<void> {
        if (synologyPanel.visible) {
          synologyPanel.visible = false;
          relayout(page);
          return;
        }
        try {
          const info = await crm.getEnrollmentInstaller(customerId);
          if (ctx.cancelled()) return;
          synologyUrl.text = info.serverUrl ?? '';
          synologyToken.text = info.token ?? '';
          synologyPanel.visible = true;
          relayout(page);
        } catch {
          if (!ctx.cancelled()) status.error('Could not load the enrollment token.');
        }
      }

      // =================================================================
      //  Employees
      // =================================================================

      function beginNewEmployee(): void {
        editingEmployeeId = 0;
        employeeEditorTitle.text = 'New employee';
        empName.text = '';
        empEmail.text = '';
        empPhone.text = '';
        empTitle.text = '';
        employeeEditor.visible = true;
        relayout(page);
        empName.focus();
      }

      function beginEditEmployee(e: EmployeeDto): void {
        editingEmployeeId = e.id;
        employeeEditorTitle.text = 'Edit employee';
        empName.text = e.fullName;
        empEmail.text = e.email;
        empPhone.text = e.phone;
        empTitle.text = e.title;
        employeeEditor.visible = true;
        relayout(page);
        empName.focus();
      }

      function closeEmployeeEditor(): void {
        employeeEditor.visible = false;
        relayout(page);
      }

      async function saveEmployee(): Promise<void> {
        const fullName = empName.text.trim();
        if (!fullName) {
          status.error('Employee name is required.');
          return;
        }
        status.busy('Saving…');
        try {
          if (editingEmployeeId === 0) {
            await crm.createEmployee({
              customerId,
              fullName,
              email: empEmail.text.trim(),
              phone: empPhone.text.trim(),
              title: empTitle.text.trim()
            });
          } else {
            await crm.updateEmployee(editingEmployeeId, {
              fullName,
              email: empEmail.text.trim(),
              phone: empPhone.text.trim(),
              title: empTitle.text.trim()
            });
          }
          if (ctx.cancelled()) return;
          status.ok(`Saved ${fullName}.`);
          closeEmployeeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function removeEmployee(e: EmployeeDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Remove employee',
          message: `Remove ${e.fullName}?`
        });
        if (!ok || ctx.cancelled()) return;
        try {
          await crm.deleteEmployee(e.id);
          if (ctx.cancelled()) return;
          status.ok(`Removed ${e.fullName}.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      // =================================================================
      //  Approval
      // =================================================================

      async function approve(
        d: DeviceDto,
        employeeIds: number[],
        primaryEmployeeId: number | null,
        rowStatus: StatusLine
      ): Promise<void> {
        rowStatus.busy('Approving…');
        try {
          await crm.approveDevice(d.id, employeeIds, primaryEmployeeId);
          if (ctx.cancelled()) return;
          status.ok(`Approved ${d.name}.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) rowStatus.error(describeError(error));
        }
      }

      async function reject(d: DeviceDto, rowStatus: StatusLine): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Reject device',
          message: `Reject ${d.name}? It won't be managed.`
        });
        if (!ok || ctx.cancelled()) return;
        rowStatus.busy('Rejecting…');
        try {
          await crm.rejectDevice(d.id);
          if (ctx.cancelled()) return;
          status.ok(`Rejected ${d.name}.`);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) rowStatus.error(describeError(error));
        }
      }

      function toggleDiagnostics(): void {
        diagnosticsExpanded = !diagnosticsExpanded;
        diagnosticsHost.visible = diagnosticsExpanded;
        diagnosticsToggle.text = diagnosticsExpanded ? 'Hide' : 'Show';
        relayout(page);
      }

      // =================================================================
      //  Tickets
      // =================================================================

      function beginNewTicket(): void {
        ticketTitle.text = '';
        ticketDescription.text = '';
        ticketPriority.selectedIndex = 1;
        ticketReporter.selectedIndex = -1;
        ticketEditor.visible = true;
        relayout(page);
        ticketTitle.focus();
      }

      function closeTicketEditor(): void {
        ticketEditor.visible = false;
        relayout(page);
      }

      async function saveTicket(): Promise<void> {
        const title = ticketTitle.text.trim();
        if (!title) {
          status.error('A ticket needs a title.');
          return;
        }
        status.busy('Raising…');
        try {
          const reporterIndex = ticketReporter.selectedIndex;
          const created = await crm.createTicket({
            customerId,
            deviceId: null,
            title,
            description: ticketDescription.text.trim(),
            priority: PRIORITIES[Math.max(0, ticketPriority.selectedIndex)]!,
            reporterEmployeeId:
              reporterIndex >= 0 && reporterIndex < employees.length
                ? employees[reporterIndex]!.id
                : null
          });
          if (ctx.cancelled()) return;
          closeTicketEditor();
          status.ok(
            `Raised ticket #${created.id} - ${created.title}. Time and charges can now be logged against it.`
          );
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      function toggleClosedTickets(): void {
        showClosedTickets = !showClosedTickets;
        closedToggle.text = showClosedTickets ? 'Hide closed' : 'Show closed';
        applyTicketFilter();
      }

      /**
       * Open work first, then by most recently touched.
       *
       * Resolved and closed tickets are hidden behind a toggle rather than dropped: work is often
       * written up after a ticket closes, and the entry still belongs to that job.
       */
      function applyTicketFilter(): void {
        const shown = allTickets
          .filter(t => showClosedTickets || !isFinished(t.status))
          .sort((a, b) => {
            const finished = Number(isFinished(a.status)) - Number(isFinished(b.status));
            if (finished !== 0) return finished;
            return b.updatedUtc.localeCompare(a.updatedUtc);
          });
        ticketList.set(shown);
        ticketsEmpty.update(shown.length, showClosedTickets ? 'No tickets yet.' : 'No open tickets.');

        const open = allTickets.filter(t => !isFinished(t.status)).length;
        ticketSummary.text =
          allTickets.length === 0 ? 'No tickets yet.' : `${open} open of ${allTickets.length}`;
        relayout(page);
      }

      // =================================================================
      //  Contract pricing
      // =================================================================

      // The existing prices always show and load with the page; only the editor is behind the toggle.
      async function togglePricing(): Promise<void> {
        pricingEditor.visible = !pricingEditor.visible;
        relayout(page);
        if (pricingEditor.visible) await refreshPricing();
      }

      async function refreshPricing(): Promise<void> {
        try {
          const selectedId = catalogIds[pricingItems.selectedIndex];
          const items = await crm.getCatalog(false, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          catalogIds = items.map(i => i.id);
          pricingItems.items = items.map(i => join([i.name, i.sku]));
          pricingItems.selectedIndex =
            selectedId === undefined ? -1 : Math.max(-1, catalogIds.indexOf(selectedId));
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
        await refreshContractPrices();
      }

      async function refreshContractPrices(): Promise<void> {
        try {
          const prices = await crm.getContractPrices(customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          pricingList.set(prices);
          pricingEmpty.update(prices.length);
          relayout(page);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function savePrice(): Promise<void> {
        const index = pricingItems.selectedIndex;
        if (index < 0 || index >= catalogIds.length) {
          status.error('Pick a catalog item to price.');
          return;
        }
        const price = pricingAmount.value();
        if (price === null || price < 0) {
          status.error('Enter a contract price of zero or more.');
          return;
        }
        try {
          const saved = await crm.setContractPrice(customerId, catalogIds[index]!, price);
          if (ctx.cancelled()) return;
          status.ok(`${saved.itemName} is ${money(price)} for this customer.`);
          pricingAmount.set(null);
          await refreshPricing();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function clearPrice(p: import('../contracts.gen.js').CustomerCatalogPriceDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Remove contract price',
          message: `Remove the contract price for "${p.itemName}"? It goes back to ${money(
            p.standardUnitPrice
          )}.`
        });
        if (!ok || ctx.cancelled()) return;
        try {
          await crm.clearContractPrice(customerId, p.catalogItemId);
          if (ctx.cancelled()) return;
          status.ok(`${p.itemName} is back to the standard price.`);
          await refreshContractPrices();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      // =================================================================
      //  Loading
      // =================================================================

      async function refreshDiagnostics(): Promise<void> {
        // Wrapped apart from its neighbours, and reported rather than swallowed: this is the newest
        // surface on the page and the only one whose endpoint a deployed server may predate, so a
        // 404 from an older API must not take the rest of the customer page down with it.
        try {
          const reports = await crm.getAgentDiagnostics(customerId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          diagnosticsHost.controls.clear();
          for (const report of reports) diagnosticsHost.controls.add(buildDiagnosticCard(report));
          diagnosticsCard.visible = reports.length > 0;
          diagnosticsHost.visible = diagnosticsExpanded;
          diagnosticsSummary.text =
            reports.length === 1
              ? 'Agents that could not enroll - 1 machine reported a failed enrollment'
              : `Agents that could not enroll - ${reports.length} machines reported a failed enrollment`;
        } catch (error) {
          if (!ctx.cancelled()) status.error(`Could not load agent diagnostics: ${describeError(error)}`);
        }
      }

      /** Only the two lists a recurring template can change - see the note at the top of this file. */
      async function refreshTicketsAndInvoices(): Promise<void> {
        try {
          const [tickets, invoices] = await Promise.all([
            crm.getTickets(customerId, ctx.dispose.signal()),
            crm.getCustomerInvoices(customerId, ctx.dispose.signal())
          ]);
          if (ctx.cancelled()) return;
          allTickets = tickets;
          applyTicketFilter();

          const newestFirst = [...invoices].sort((a, b) => b.createdUtc.localeCompare(a.createdUtc));
          invoiceList.set(newestFirst);
          invoiceSection.visible = newestFirst.length > 0;
          await uninvoiced.refresh();
          relayout(page);
        } catch (error) {
          if (!ctx.cancelled()) {
            status.error(`Could not refresh tickets and invoices: ${describeError(error)}`);
          }
        }
      }

      async function refresh(): Promise<void> {
        try {
          const [people, devices, monitors, pending] = await Promise.all([
            crm.getEmployees(customerId, ctx.dispose.signal()),
            crm.getDevices(customerId, ctx.dispose.signal()),
            crm.getCustomerMonitors(customerId, ctx.dispose.signal()),
            crm.getPendingDevices(customerId, ctx.dispose.signal())
          ]);
          if (ctx.cancelled()) return;

          employees = people;
          employeeList.set(people);
          employeesEmpty.update(people.length);
          ticketReporter.items = people.map(e => e.fullName);

          const approved = devices.filter(d => d.status === ApprovalStatus.Approved);
          deviceList.set(approved);
          devicesEmpty.update(approved.length);

          // Only the agentless monitors. One whose machine also runs the agent is listed above as an
          // enrolled device, and its SNMP data lives on that device's SNMP tab - listing it here as
          // well would put one Synology on this page twice.
          const agentless = monitors.filter(m => !m.hasAgent);
          monitorList.set(agentless);
          monitorSection.visible = agentless.length > 0;

          pendingHost.controls.clear();
          for (const d of pending) pendingHost.controls.add(buildPendingCard(d));
          pendingCard.visible = pending.length > 0;

          await refreshDiagnostics();
          await refreshTicketsAndInvoices();
          await refreshContractPrices();
          relayout(page);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function load(): Promise<void> {
        try {
          applyCustomer(await crm.getCustomer(customerId, ctx.dispose.signal()));
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
          return;
        }
        if (ctx.cancelled()) return;
        await refresh();
      }

      await load();
      if (ctx.cancelled()) return;

      // The recurring sections load after the page's own lists and report into their own status
      // lines rather than failing the page: they are the newest surfaces here, and this page is
      // entirely usable without them.
      await recurringTickets.initialize();
      if (ctx.cancelled()) return;
      await recurringInvoices.initialize();
      if (ctx.cancelled()) return;
      await androidPhones.load();
      if (ctx.cancelled()) return;
      await windowsPolicies.load();
      if (ctx.cancelled()) return;
      await attachments.refresh();
      relayout(content);

      // A machine that just failed to enroll uploads its report while someone may be looking at this
      // page. Only the diagnostics card is reloaded: a full refresh would rebuild every list and
      // discard whatever editor is open, because an unrelated machine happened to report. Live
      // updates are a nicety - the page is complete over REST - so a hub that will not connect is
      // not an error here.
      if (ctx.cancelled() || !(await hub.ensureConnected()) || ctx.cancelled()) return;
      ctx.dispose.add(
        hub.on('AgentDiagnosticsChanged', changed => {
          if (changed !== customerId) return;
          void refreshDiagnostics().then(() => relayout(diagnosticsCard));
        })
      );
    }
  };
}
